import AlertBanner from "components/AlertBanner";
import DeviceSelector from "components/DeviceSelector";
import AWDStatusCard from "components/AWD/AWDStatusCard";
import AWDCycleCalendar from "components/AWD/AWDCycleCalendar";
import AWDCycleList from "components/AWD/AWDCycleList";
import CropCyclePanel from "components/AWD/CropCyclePanel";
import AWDConfigPanel from "components/AWD/AWDConfigPanel";
import { useDevices } from "hooks/useDevices";
import { Database, Droplets, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import {
  getAWDStatus,
  getAWDCycles,
  getAWDCycleDetail,
  getAWDConfig,
  getCropCycle,
} from "services/api";

const WaterManagementPage = () => {
  const { devices, selectedDevice, setSelectedDevice, refetch: refetchDevices } = useDevices();

  const [awdStatus, setAwdStatus] = useState(null);
  const [awdCycles, setAwdCycles] = useState([]);
  const [awdConfig, setAwdConfig] = useState(null);
  const [cropCycle, setCropCycle] = useState(null);
  const [awdLoading, setAwdLoading] = useState(false);
  const [highlightedCycleId, setHighlightedCycleId] = useState(null);
  const [error, setError] = useState(null);

  const fetchAWDData = async (mac) => {
    setAwdLoading(true);
    setError(null);
    try {
      const [statusData, cyclesData, configData, cropData] = await Promise.allSettled([
        getAWDStatus(mac),
        getAWDCycles(mac, "limit=100"),
        getAWDConfig(mac),
        getCropCycle(mac),
      ]);
      if (statusData.status === "fulfilled") setAwdStatus(statusData.value);
      if (cyclesData.status === "fulfilled") setAwdCycles(cyclesData.value.cycles || []);
      if (configData.status === "fulfilled") setAwdConfig(configData.value.awd_config);
      if (cropData.status === "fulfilled") setCropCycle(cropData.value.crop_cycle);
    } catch (err) {
      setError("Failed to load water management data");
      console.error("Error fetching AWD data:", err);
    } finally {
      setAwdLoading(false);
    }
  };

  const handleCycleClick = async (cycle) => {
    setHighlightedCycleId(cycle.id);
    if (!cycle.events) {
      try {
        const data = await getAWDCycleDetail(selectedDevice, cycle.id);
        setAwdCycles((prev) =>
          prev.map((c) => (c.id === cycle.id ? { ...c, events: data.cycle.events } : c))
        );
      } catch (err) {
        console.error("Error fetching cycle detail:", err);
      }
    }
  };

  useEffect(() => {
    if (selectedDevice) fetchAWDData(selectedDevice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice]);

  if (!selectedDevice) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-on-surface-variant">No devices available</p>
          <button
            onClick={refetchDevices}
            className="mt-4 px-4 py-2 bg-primary text-on-primary rounded-full hover:bg-primary-container"
          >
            Refresh Devices
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold font-headline text-on-surface tracking-tight">
            Water Management
          </h1>
          <p className="text-on-surface-variant mt-1">
            Alternate Wetting and Drying (AWD) cycle tracking
          </p>
        </div>
        <div className="min-w-[200px]">
          <p className="text-[10px] font-bold text-on-surface-variant uppercase tracking-widest mb-1">
            Active Node
          </p>
          <DeviceSelector
            devices={devices}
            value={selectedDevice}
            onChange={setSelectedDevice}
            placeholder=""
          />
        </div>
      </div>

      <AlertBanner type="error" message={error} onDismiss={() => setError(null)} />

      {/* Refresh button */}
      <div className="flex justify-end">
        <button
          onClick={() => fetchAWDData(selectedDevice)}
          disabled={awdLoading}
          className="px-4 py-2 bg-primary text-on-primary rounded-full hover:bg-primary-container disabled:opacity-50 flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${awdLoading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Status + config row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1">
          <AWDStatusCard awdStatus={awdStatus} activeCycle={awdStatus?.active_cycle} />
        </div>
        <div className="lg:col-span-1">
          <CropCyclePanel
            mac={selectedDevice}
            cropCycle={cropCycle}
            onSaved={() => fetchAWDData(selectedDevice)}
          />
        </div>
        <div className="lg:col-span-1">
          <AWDConfigPanel
            mac={selectedDevice}
            awdConfig={awdConfig}
            onSaved={() => fetchAWDData(selectedDevice)}
          />
        </div>
      </div>

      {/* Calendar */}
      <div>
        <h4 className="text-sm font-semibold text-gray-600 mb-2">Cycle Calendar</h4>
        <AWDCycleCalendar
          cycles={awdCycles}
          cropCycle={cropCycle}
          onCycleClick={handleCycleClick}
        />
      </div>

      {/* Cycle list */}
      <div>
        <h4 className="text-sm font-semibold text-gray-600 mb-2">
          AWD Cycles ({awdCycles.length})
        </h4>
        <AWDCycleList
          cycles={awdCycles}
          highlightedCycleId={highlightedCycleId}
          onCycleSelect={handleCycleClick}
        />
      </div>

      {awdLoading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      )}
    </div>
  );
};

export default WaterManagementPage;
